<script lang="ts" generics="T extends string | number">
  let {
    id,
    value = $bindable(),
    options,
    disabled = false,
    onchange,
    'aria-label': label,
  }: {
    id: string;
    value: T;
    /** Evenly spaced notches in display order; the slider moves by option index. */
    options: readonly { value: T; label: string }[];
    disabled?: boolean;
    onchange?: (value: T) => void;
    'aria-label'?: string;
  } = $props();

  // A stored value between notches (older configs, custom durations) shows at the nearest one
  // and only changes when the player moves the slider.
  const index = $derived.by(() => {
    const exact = options.findIndex((option) => option.value === value);
    if (exact !== -1 || typeof value !== 'number') return Math.max(exact, 0);
    let nearest = 0;
    for (let i = 1; i < options.length; i++) {
      const candidate = options[i]!.value as number;
      if (Math.abs(candidate - value) < Math.abs((options[nearest]!.value as number) - value)) nearest = i;
    }
    return nearest;
  });
  const current = $derived(options[index]!);

  function move(event: Event): void {
    const option = options[Number((event.currentTarget as HTMLInputElement).value)];
    if (!option || option.value === value) return;
    value = option.value;
    onchange?.(value);
  }
</script>

<div class="notched" style:--notches={options.length}>
  <div class="notched-track">
    <div class="notched-stops notched-ticks" aria-hidden="true">
      {#each options as option, i (option.value)}
        <span class="notched-tick" class:passed={i <= index}></span>
      {/each}
    </div>
    <input
      {id}
      class="notched-input"
      type="range"
      min="0"
      max={options.length - 1}
      step="1"
      value={index}
      {disabled}
      aria-label={label}
      aria-valuetext={current.label}
      oninput={move}
    />
  </div>
  <div class="notched-stops notched-labels" aria-hidden="true">
    {#each options as option, i (option.value)}
      <span class="notched-label" class:current={i === index}>{option.label}</span>
    {/each}
  </div>
</div>

<style>
  .notched {
    --thumb: 28px;
    --track: 3px;
    /* 2px above the 44px target floor so sub-pixel layout rounding cannot dip under it. */
    --hit: 46px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    min-width: 0;
  }
  .notched-track {
    position: relative;
    height: var(--hit);
  }
  /*
   * The thumb's centre travels from thumb/2 to width - thumb/2, so the stop spacing is
   * (width - thumb) / (notches - 1). A grid of equal columns whose centres fall on those stops
   * needs this negative horizontal margin; ticks and labels share it so they always agree.
   */
  .notched-stops {
    display: grid;
    grid-template-columns: repeat(var(--notches), minmax(0, 1fr));
    margin: 0 calc(var(--thumb) / 2 - (100% - var(--thumb)) / (2 * (var(--notches) - 1)));
  }
  .notched-ticks {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  .notched-tick {
    justify-self: center;
    align-self: center;
    width: 2px;
    height: 12px;
    background: var(--theme-border);
  }
  .notched-tick.passed {
    background: var(--theme-selected);
  }
  .notched-input {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    display: block;
    width: 100%;
    height: var(--hit);
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
    background: linear-gradient(var(--theme-border), var(--theme-border)) center / calc(100% - var(--thumb))
      var(--track) no-repeat;
    cursor: pointer;
  }
  .notched-input:disabled {
    cursor: not-allowed;
  }
  /* The thumb is a selected button in miniature so each pack's shape and texture carry over. */
  .notched-input::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;
    width: var(--thumb);
    height: var(--thumb);
    border: var(--theme-button-border);
    border-radius: var(--theme-button-radius);
    background: var(--theme-button-texture), var(--theme-selected);
    background-size: var(--theme-button-texture-size), auto;
    box-shadow: var(--theme-control-shadow);
  }
  .notched-input::-moz-range-thumb {
    width: var(--thumb);
    height: var(--thumb);
    border: var(--theme-button-border);
    border-radius: var(--theme-button-radius);
    background: var(--theme-button-texture), var(--theme-selected);
    background-size: var(--theme-button-texture-size), auto;
    box-shadow: var(--theme-control-shadow);
  }
  .notched-input:focus-visible {
    outline: var(--theme-focus-width) solid var(--theme-focus);
    outline-offset: 2px;
  }
  .notched-labels {
    font-family: var(--theme-field-font);
    font-size: 12px;
    letter-spacing: var(--theme-field-letter-spacing);
    color: var(--theme-muted-text);
  }
  .notched-label {
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .notched-label.current {
    color: var(--theme-surface-text);
    font-weight: 600;
  }
</style>
